import type { CountryState } from '@firsttable/types';
import { countFormat, roundToClosest } from '@discover-lib/dataFormats';

export type PD6630Location = {
  id: number;
  name: string;
  slug: string;
  restaurantCount: number | null;
  imagePath: string | null;
  timeZone: string | null;
};

export type PD6630Region = {
  id?: number | null;
  slug?: string | null;
  comingSoon?: boolean | null;
  timeZone?: string | null;
};

export function getPD6630Locations(
  countryStates: CountryState[],
  regions: readonly PD6630Region[]
): PD6630Location[] {
  const comingSoonSlugs = new Set(regions.filter((r) => r.comingSoon).map((r) => r.slug));
  // The city page reads timezone by region id. Slug is only a fallback for the same string.
  const timeZoneById = new Map<number, string>();
  const timeZoneBySlug = new Map<string, string>();
  regions.forEach((region) => {
    if (!region.timeZone) return;
    if (region.id != null && !timeZoneById.has(region.id)) {
      timeZoneById.set(region.id, region.timeZone);
    }
    if (region.slug && !timeZoneBySlug.has(region.slug)) {
      timeZoneBySlug.set(region.slug, region.timeZone);
    }
  });
  const locations = new Map<string, PD6630Location>();

  countryStates
    .flatMap((countryState) => countryState.cities ?? [])
    .forEach((city) => {
      const slug = city?.slug;
      const name = city?.menuTitle || city?.title;
      if (city?.id == null || !slug || !name || city.comingSoon || comingSoonSlugs.has(slug)) {
        return;
      }
      // Country states also include suburbs. Locations are the top-level cities.
      if (slug.split('/').filter(Boolean).length !== 1 || locations.has(slug)) return;

      locations.set(slug, {
        id: city.id,
        name,
        slug,
        restaurantCount: city.restaurantCount ?? null,
        imagePath: city.scenicPhoto?.basePath ?? null,
        timeZone: timeZoneById.get(city.id) ?? timeZoneBySlug.get(slug) ?? null,
      });
    });

  return [...locations.values()];
}

export function findPD6630KnownLocation(
  locations: readonly PD6630Location[],
  savedSlug: string | null
): PD6630Location | undefined {
  if (!savedSlug) return undefined;
  const direct = locations.find((location) => location.slug === savedSlug);
  if (direct) return direct;
  // A saved suburb slug such as /queenstown-lakes/arrowtown belongs to its parent city.
  const parentSegment = savedSlug.split('/').filter(Boolean)[0];
  if (!parentSegment) return undefined;
  return locations.find((location) => location.slug === `/${parentSegment}`);
}

export function formatPD6630RestaurantCount(count: number | null) {
  if (!count) return null;
  if (count < 10) return `${count} restaurant${count === 1 ? '' : 's'}`;
  return `${countFormat(roundToClosest(count, 10 ** (String(count).length - 1)))}+ restaurants`;
}
